import React, { useMemo, useState } from 'react';
import { ComponentType } from 'react';
import { AlertTriangleIcon, ArrowLeftRightIcon, BarChart3Icon, BoxesIcon, CalendarX2Icon, CircleDollarSignIcon, FileTextIcon, HistoryIcon, PackageCheckIcon, PackageXIcon, PillIcon, ShieldCheckIcon, SlidersHorizontalIcon, TriangleAlertIcon, TruckIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { PharmacySection } from './PharmacyModule';
import { expiryAlertWindow, formatCurrency, isLowStockOnly, isOutOfStock, isToday } from './pharmacyUtils';
type Props = {
  onNavigate: (section: PharmacySection) => void;
};
type ControlGroup = {
  label: string;
  items: {
    id: PharmacySection;
    label: string;
    description: string;
    icon: ComponentType<{
      className?: string;
    }>;
  }[];
};
export function PharmacistDashboard({
  onNavigate
}: Props) {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    suppliers,
    pharmacySettings,
    updatePharmacySettings
  } = useData();
  const [savingPolicy, setSavingPolicy] = useState(false);
  const stats = useMemo(() => {
    const expiry = (window: Exclude<ReturnType<typeof expiryAlertWindow>, null>) => medicineBatches.filter(batch => expiryAlertWindow(batch) === window);
    return {
      lowStock: medicines.filter(isLowStockOnly).length,
      outOfStock: medicines.filter(isOutOfStock).length,
      expired: expiry('Expired').length,
      days30: expiry('30 Days').length,
      days60: expiry('60 Days').length,
      days90: expiry('90 Days').length,
      receivedToday: stockMovements.filter(movement => movement.type === 'Receipt' && isToday(movement.createdAt)).length,
      adjustmentsToday: stockMovements.filter(movement => movement.type === 'Adjustment' && isToday(movement.createdAt)).length,
      pendingOrders: purchaseOrders.filter(order => order.status === 'Pending').length,
      value: medicines.reduce((sum, medicine) => sum + medicine.currentQuantity * medicine.costPrice, 0)
    };
  }, [medicineBatches, medicines, purchaseOrders, stockMovements]);
  const updatePolicy = async (allowMedicineReturns: boolean) => {
    setSavingPolicy(true);
    await updatePharmacySettings({
      allowMedicineReturns
    });
    setSavingPolicy(false);
  };
  const cards = [{
    label: 'Medicines on file',
    value: medicines.length,
    icon: PillIcon,
    tone: 'blue'
  }, {
    label: 'Inventory value',
    value: formatCurrency(stats.value),
    icon: CircleDollarSignIcon,
    tone: 'emerald'
  }, {
    label: 'Received today',
    value: stats.receivedToday,
    icon: BoxesIcon,
    tone: 'teal'
  }, {
    label: 'Pending purchase orders',
    value: stats.pendingOrders,
    icon: FileTextIcon,
    tone: 'blue'
  }, {
    label: 'Low stock',
    value: stats.lowStock,
    icon: AlertTriangleIcon,
    tone: 'amber'
  }, {
    label: 'Out of stock',
    value: stats.outOfStock,
    icon: PackageXIcon,
    tone: 'red'
  }, {
    label: 'Expired batches',
    value: stats.expired,
    icon: TriangleAlertIcon,
    tone: 'red'
  }, {
    label: 'Approved suppliers',
    value: suppliers.length,
    icon: TruckIcon,
    tone: 'teal'
  }] as const;
  const controls: ControlGroup[] = [{
    label: 'Pharmacy',
    items: [{
      id: 'inventory',
      label: 'Inventory',
      description: 'Medicine register, reorder levels, and batch stock.',
      icon: PillIcon
    }, {
      id: 'receive-stock',
      label: 'Receive New Stock',
      description: 'Enter supplier, invoice, batch, pricing, and expiry.',
      icon: PackageCheckIcon
    }, {
      id: 'adjustments',
      label: 'Stock Adjustments',
      description: 'Correct counts with an audited reason.',
      icon: SlidersHorizontalIcon
    }, {
      id: 'returns',
      label: 'Medicine Returns',
      description: 'Accept returns under the current hospital policy.',
      icon: ArrowLeftRightIcon
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      description: 'Quarantine and dispose of expired batches.',
      icon: CalendarX2Icon
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      description: 'Items at or below reorder level.',
      icon: AlertTriangleIcon
    }]
  }, {
    label: 'Procurement',
    items: [{
      id: 'purchase-orders',
      label: 'Purchase Orders',
      description: 'Raise, track, and close supplier orders.',
      icon: FileTextIcon
    }, {
      id: 'suppliers',
      label: 'Suppliers',
      description: 'Maintain supplier records and contacts.',
      icon: TruckIcon
    }]
  }, {
    label: 'Insights',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      description: 'Valuation, consumption, and expiry reporting.',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      description: 'Every receipt, dispense, adjustment, and return.',
      icon: HistoryIcon
    }]
  }];
  return <div className="space-y-6" data-id="element-4802" data-mp="1ajnmp7bkv0rz">
      <section className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-4803" data-mp="1aim2ac8o91xl">
        <div data-id="element-4804" data-mp="1fh75mkj801q9">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4805" data-mp="2lzcb834hc7uw">
            Pharmacist · {currentUser?.fullName}
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl" data-id="element-4806" data-mp="2qxnmmiafaqf8">
            Administrative control of the pharmacy.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-4807" data-mp="qy0j0qyn6xcx">
            You own goods receiving, stock corrections, procurement, and policy.
            Every entry you record updates the live inventory ledger.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-4808" data-mp="vwbu150obdwq">
          <button type="button" onClick={() => onNavigate('receive-stock')} className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2" data-id="element-4809" data-mp="vtfujc96gh66">
            <PackageCheckIcon className="mr-1.5 h-4 w-4" data-id="element-4810" data-mp="c6rmdxwbobqa" /> Receive new stock
          </button>
          <button type="button" onClick={() => onNavigate('purchase-orders')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2" data-id="element-4811" data-mp="3kmuqxus51kae">
            <FileTextIcon className="mr-1.5 h-4 w-4" data-id="element-4812" data-mp="3b9ubjjmongdv" /> Purchase orders
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Pharmacy key figures" data-id="element-4813" data-mp="2whaduyiay13t">
        {cards.map(({
        label,
        value,
        icon: Icon,
        tone
      }) => <article key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4814" data-mp="3p5zeyxjcy6fk">
            <div className="flex items-start justify-between gap-3" data-id="element-4815" data-mp="3faebeuyn4y3p">
              <div data-id="element-4816" data-mp="3fc8tm1ij2c43">
                <p className="text-sm font-medium text-slate-500" data-id="element-4817" data-mp="3k8pmf90o9eni">{label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-4818" data-mp="3kask8f82gc2s">
                  {value}
                </p>
              </div>
              <div className={`rounded-lg p-2.5 ${iconTone(tone)}`} data-id="element-4819" data-mp="28uvljzafedsd">
                <Icon className="h-5 w-5" data-id="element-4820" data-mp="1q2zzg4o8rcj7" />
              </div>
            </div>
          </article>)}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.85fr]" data-id="element-4821" data-mp="1l35a45k46v09">
        <div className="space-y-6" data-id="element-4822" data-mp="1g4kaa78rydcc">
          {controls.map(group => <div key={group.label} className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4823" data-mp="1c3i9vn34fv8s">
              <div className="border-b border-slate-100 px-5 py-4" data-id="element-4824" data-mp="28spy52v4kige">
                <h3 className="font-bold text-slate-900" data-id="element-4825" data-mp="23uy15ufmi65c">{group.label}</h3>
                <p className="mt-0.5 text-sm text-slate-500" data-id="element-4826" data-mp="23x0til490bd8">
                  Full administrative rights.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2" data-id="element-4827" data-mp="1v1bd6qhnq76p">
                {group.items.map(item => {
              const Icon = item.icon;
              return <button key={item.id} type="button" onClick={() => onNavigate(item.id)} className="flex items-start gap-3 bg-white p-4 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500" data-id="element-4828" data-mp="2msheha946axk">
                      <span className="rounded-lg bg-teal-50 p-2 text-teal-700" data-id="element-4829" data-mp="idxcnzw2k254">
                        <Icon className="h-4 w-4" data-id="element-4830" data-mp="nbytsf9xprdy" />
                      </span>
                      <span className="min-w-0" data-id="element-4831" data-mp="s8yzuq6sau52">
                        <span className="block text-sm font-semibold text-slate-900" data-id="element-4832" data-mp="x8lgl9ltmsim">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-sm text-slate-500" data-id="element-4833" data-mp="1203hjxnugt3l">
                          {item.description}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </div>)}
        </div>

        <aside className="space-y-5" data-id="element-4834" data-mp="1fzmctpvk49yh">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4835" data-mp="2b63auqk2uglw">
            <div className="border-b border-slate-100 px-5 py-4" data-id="element-4836" data-mp="2g4xqjjmqzp1s">
              <h3 className="font-bold text-slate-900" data-id="element-4837" data-mp="2g5fyvemoptq2">Attention required</h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-4838" data-mp="2k5giresjyx4i">
                Counts update as soon as a stock transaction commits.
              </p>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-4839" data-mp="2twkywhtyso0x">
              <AlertRow label="Out of stock" count={stats.outOfStock} tone="red" onClick={() => onNavigate('low-stock')} data-id="element-4840" data-mp="2tw2w12csrbk1" />
              <AlertRow label="At or below reorder level" count={stats.lowStock} tone="amber" onClick={() => onNavigate('low-stock')} data-id="element-4841" data-mp="33va69go8iq2x" />
              <AlertRow label="Expired — dispensing blocked" count={stats.expired} tone="red" onClick={() => onNavigate('expired')} data-id="element-4842" data-mp="19ikasui7x8tf" />
              <AlertRow label="Expiry alerts · 30 / 60 / 90 days" count={stats.days30 + stats.days60 + stats.days90} tone="amber" detail={`${stats.days30} at 30d · ${stats.days60} at 60d · ${stats.days90} at 90d`} onClick={() => onNavigate('expired')} data-id="element-4843" data-mp="117trcxbz5d7c" />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4844" data-mp="1ovbn13yd0vfl">
            <div className="flex items-center gap-3" data-id="element-4845" data-mp="1jz4fj6qbfbvj">
              <span className="rounded-lg bg-teal-50 p-2 text-teal-700" data-id="element-4846" data-mp="1fw81ltbq03xx">
                <ShieldCheckIcon className="h-5 w-5" data-id="element-4847" data-mp="1axn4i2rrm0dp" />
              </span>
              <div data-id="element-4848" data-mp="1axd89xg4mwzk">
                <h3 className="font-bold text-slate-900" data-id="element-4849" data-mp="4hkstfas47j2">
                  Medicine return policy
                </h3>
                <p className="text-sm text-slate-500" data-id="element-4850" data-mp="3vnchsbctle4a">
                  Last updated by {pharmacySettings.updatedByName}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3" data-id="element-4851" data-mp="5yq6apuvxvzm">
              <span className="text-sm font-medium text-slate-700" data-id="element-4852" data-mp="3o4mpxxq1y1sq">
                Allow medicine returns
              </span>
              <button type="button" role="switch" aria-checked={pharmacySettings.allowMedicineReturns} aria-label="Allow medicine returns" disabled={savingPolicy} onClick={() => updatePolicy(!pharmacySettings.allowMedicineReturns)} className={`relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${pharmacySettings.allowMedicineReturns ? 'bg-teal-600' : 'bg-slate-300'}`} data-id="element-4853" data-mp="3s4wv5860so6j">
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${pharmacySettings.allowMedicineReturns ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-4854" data-mp="30fa869gnslet" />
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500" data-id="element-4855" data-mp="2bw00xej06j0w">
              As Pharmacist you are the only role that can change this hospital
              policy.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4856" data-mp="2lqs3yxvcty32">
            <h3 className="font-bold text-slate-900" data-id="element-4857" data-mp="2zq9vhl01bpg4">Today’s activity</h3>
            <div className="mt-4 space-y-3" data-id="element-4858" data-mp="2urq8dh9pg7m6">
              <DeskMetric label="Stock received" value={stats.receivedToday} onClick={() => onNavigate('movement-history')} data-id="element-4859" data-mp="2uqnf52owtt33" />
              <DeskMetric label="Adjustments recorded" value={stats.adjustmentsToday} onClick={() => onNavigate('adjustments')} data-id="element-4860" data-mp="34jco9qwzmuv9" />
              <DeskMetric label="Purchase orders pending" value={stats.pendingOrders} onClick={() => onNavigate('purchase-orders')} data-id="element-4861" data-mp="3dl2hn2afk3vo" />
            </div>
          </div>
        </aside>
      </section>
    </div>;
}
function AlertRow({
  label,
  count,
  tone,
  detail,
  onClick
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber';
  detail?: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500" data-id="element-4862" data-mp="svzh6vz4q0zv">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone === 'red' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-4863" data-mp="11v4ey02v9qaz">
        <AlertTriangleIcon className="h-4 w-4" data-id="element-4864" data-mp="2dbudqy4ynfuk" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-4865" data-mp="2ddf7wmnd4wrw">
        <span className="block text-sm font-semibold text-slate-900" data-id="element-4866" data-mp="2i9comu2jg557">
          {label}
        </span>
        <span className="mt-0.5 block text-sm text-slate-500" data-id="element-4867" data-mp="2secemhzff6ch">
          {detail || 'Live inventory control indicator'}
        </span>
      </span>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-4868" data-mp="2nh2nmxwe2mmj">
        {count}
      </span>
    </button>;
}
function DeskMetric({
  label,
  value,
  onClick
}: {
  label: string;
  value: number;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left hover:border-teal-200 hover:bg-teal-50/40 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-4869" data-mp="24nyrn5vp7xor">
      <span className="text-sm font-medium text-slate-700" data-id="element-4870" data-mp="1vq6cq2tw2s1w">{label}</span>
      <span className="text-xl font-bold text-slate-950" data-id="element-4871" data-mp="3u2n57ag0zlod">{value}</span>
    </button>;
}
function iconTone(tone: string) {
  return {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    teal: 'bg-teal-50 text-teal-700'
  }[tone] || 'bg-slate-100 text-slate-700';
}